import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as XmarkIcon } from '../../icons/xmark.svg';

import './TagInput.scss';

const TagInput = ({ name, value = [], onChange, placeholder, maxTags = 10, error, suggestions = [] }) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;

    setInputValue(newValue);

    // Filter suggestions based on input
    if (newValue.trim() && suggestions.length > 0) {
      const filtered = suggestions
        .filter(
          (suggestion) =>
            suggestion.toLowerCase().includes(newValue.toLowerCase()) && !value.includes(suggestion.toLowerCase())
        )
        .slice(0, 5); // Limit to 5 suggestions

      setFilteredSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
      setSelectedSuggestionIndex(-1);
    } else {
      setShowSuggestions(false);
      setFilteredSuggestions([]);
    }
  };

  const handleInputKeyDown = (e) => {
    // Handle arrow keys for suggestion navigation
    if (showSuggestions && filteredSuggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedSuggestionIndex((prev) => (prev < filteredSuggestions.length - 1 ? prev + 1 : prev));
        return;
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : -1));
        return;
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        return;
      }
    }

    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();

      // If a suggestion is selected, use it
      if (selectedSuggestionIndex >= 0 && filteredSuggestions[selectedSuggestionIndex]) {
        addTag(filteredSuggestions[selectedSuggestionIndex]);
      } else {
        addTag();
      }
    } else if (e.key === 'Backspace' && inputValue === '' && value.length > 0) {
      // Remove last tag when backspace is pressed on empty input
      removeTag(value.length - 1);
    }
  };

  const addTag = (suggestionValue = null) => {
    const tagToAdd = suggestionValue || inputValue.trim().toLowerCase();

    if (!tagToAdd) return;

    // Check if tag already exists
    if (value.includes(tagToAdd)) {
      setInputValue('');
      setShowSuggestions(false);
      return;
    }

    // Check max tags limit
    if (value.length >= maxTags) {
      setInputValue('');
      setShowSuggestions(false);
      return;
    }

    onChange([...value, tagToAdd]);
    setInputValue('');
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
  };

  const handleSuggestionClick = (suggestion) => {
    addTag(suggestion);
    inputRef.current?.focus();
  };

  const removeTag = (indexToRemove) => {
    onChange(value.filter((_, index) => index !== indexToRemove));
  };

  const handleContainerClick = () => {
    inputRef.current?.focus();
  };

  return (
    <div className="mg-form-group">
      <div className="tag-input-container">
        <div className={`tag-input ${error ? 'tag-input--error' : ''}`} onClick={handleContainerClick}>
          <div className="tag-input__tags">
            {value.map((tag, index) => (
              <span key={index} className="tag-input__tag">
                <span className="tag-input__label">{tag}</span>
                <button
                  type="button"
                  className="tag-input__remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeTag(index);
                  }}
                  aria-label={t('songForm.removeTag', { tag })}
                >
                  <XmarkIcon />
                </button>
              </span>
            ))}
            <input
              ref={inputRef}
              type="text"
              name={name}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleInputKeyDown}
              placeholder={value.length === 0 ? (placeholder ?? t('songForm.addTag')) : ''}
              className="tag-input__input"
            />
          </div>
        </div>

        {/* Suggestions dropdown */}
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div className="tag-suggestions" ref={suggestionsRef}>
            {filteredSuggestions.map((suggestion, index) => (
              <div
                key={suggestion}
                className={`tag-suggestion-item ${index === selectedSuggestionIndex ? 'selected' : ''}`}
                onClick={() => handleSuggestionClick(suggestion)}
              >
                {suggestion}
              </div>
            ))}
          </div>
        )}
      </div>
      {error && <small className="mg-form-text mg-form-text--error">{error}</small>}
    </div>
  );
};

export default TagInput;
